import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Button,
  Card,
  Input,
  Modal,
  Select,
  StatusBadge,
} from '@rallyshift/design-system'
import api from '../api/client'
import type { Event } from '../types'
import { Calendar, MapPin, Users, Plus, Edit, Trash2, ArrowRight } from 'lucide-react'
import { isAfter, startOfDay } from 'date-fns'
import { formatDayDateToLocal, getTimezoneAbbreviation } from '../utils/timezone'
import { useLocale } from '../contexts/LocaleContext'
import { useAuth } from '../contexts/AuthContext'
import { statusLabel } from '../utils/status'
import ImageWithFallback from '../components/ImageWithFallback'

type EventPhase = 'upcoming' | 'ongoing' | 'completed'

function phaseOf(event: Event, now: Date): EventPhase {
  if (isAfter(now, new Date(event.end_date))) return 'completed'
  if (isAfter(now, new Date(event.start_date))) return 'ongoing'
  return 'upcoming'
}

const EventsPage: React.FC = () => {
  const { t } = useLocale()
  const { isAdmin } = useAuth()
  const navigate = useNavigate()
  const [events, setEvents] = useState<Event[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'upcoming' | 'past'>('all')
  const [error, setError] = useState('')
  const [pendingDelete, setPendingDelete] = useState<Event | null>(null)
  const [deleting, setDeleting] = useState(false)

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true)
      const data = await api.events.list()
      setEvents(data || [])
    } catch (err: any) {
      console.error('Error in fetchEvents:', err)
      setError(err.message || 'Failed to fetch events')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchEvents()
  }, [fetchEvents])

  const confirmDelete = async () => {
    if (!pendingDelete) return
    setDeleting(true)
    try {
      await api.events.delete(pendingDelete.id)
      setPendingDelete(null)
      await fetchEvents()
    } catch (err: any) {
      setError(
        err?.status === 409
          ? 'Dit evenement heeft nog diensten. Verplaats of verwijder die eerst.'
          : err.message || 'Verwijderen is niet gelukt.',
      )
      setPendingDelete(null)
    } finally {
      setDeleting(false)
    }
  }

  const filteredEvents = events.filter((event) => {
    const term = searchTerm.toLowerCase()
    const matchesSearch =
      event.title.toLowerCase().includes(term) ||
      event.description.toLowerCase().includes(term) ||
      event.location.toLowerCase().includes(term)

    const now = new Date()
    const eventStartDate = new Date(event.start_date)

    let matchesStatus = true
    if (statusFilter === 'upcoming') {
      matchesStatus = isAfter(eventStartDate, startOfDay(now))
    } else if (statusFilter === 'past') {
      matchesStatus = !isAfter(eventStartDate, startOfDay(now))
    }

    return matchesSearch && matchesStatus
  })

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-rs-spin rounded-full border-2.5 border-action-primary border-r-transparent" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-display-lg text-text-primary">{t('events.title')}</h1>
          <p className="mt-1 text-md text-text-secondary">
            {filteredEvents.length} {t('events.events')}
          </p>
        </div>

        {isAdmin && (
          <Button icon={<Plus className="h-4 w-4" />} onClick={() => navigate('/events/new')}>
            Nieuw evenement
          </Button>
        )}
      </div>

      {error && (
        <Alert variant="danger" onDismiss={() => setError('')}>
          {error}
        </Alert>
      )}

      <Card>
        <div className="flex flex-col gap-4 lg:flex-row">
          <div className="flex-1">
            <Input
              label={t('common.search')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('events.searchPlaceholder')}
            />
          </div>
          <div className="lg:w-56">
            <Select
              label={t('common.filter')}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'all' | 'upcoming' | 'past')}
              options={[
                { value: 'all', label: t('events.filterAll') },
                { value: 'upcoming', label: t('events.filterUpcoming') },
                { value: 'past', label: t('events.filterPast') },
              ]}
            />
          </div>
        </div>
      </Card>

      {filteredEvents.length === 0 ? (
        <Card>
          <div className="py-10 text-center">
            <Calendar className="mx-auto h-10 w-10 text-text-muted" />
            <h3 className="mt-4 font-display text-display-sm text-text-primary">
              {t('events.noEvents')}
            </h3>
            <p className="mx-auto mt-1 max-w-md text-md text-text-secondary">
              {searchTerm || statusFilter !== 'all'
                ? t('events.noEventsFound')
                : t('events.noEventsCreated')}
            </p>
            {isAdmin && !searchTerm && statusFilter === 'all' && (
              <div className="mt-5">
                <Button
                  icon={<Plus className="h-4 w-4" />}
                  onClick={() => navigate('/events/new')}
                >
                  Nieuw evenement
                </Button>
              </div>
            )}
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
          {filteredEvents.map((event) => {
            const phase = phaseOf(event, new Date())
            const shiftCount = event.shift_count ?? 0

            return (
              <Card key={event.id} className="flex flex-col overflow-hidden !p-0">
                <button
                  type="button"
                  onClick={() => navigate(`/events/${event.id}`)}
                  className="relative block h-40 w-full overflow-hidden text-left"
                >
                  <ImageWithFallback
                    src={event.image_url}
                    alt={event.title}
                    className="h-full w-full object-cover"
                    fallbackClassName="flex h-full w-full items-center justify-center bg-surface-sunken"
                    fallbackIcon={<Calendar className="h-10 w-10 text-text-muted" />}
                  />
                  <span className="absolute left-3 top-3">
                    <StatusBadge
                      dimension="event"
                      status={phase}
                      size="sm"
                      label={statusLabel('event', phase)}
                    />
                  </span>
                </button>

                <div className="flex flex-1 flex-col gap-3 p-5">
                  <div>
                    <h3 className="line-clamp-2 font-display text-display-sm text-text-primary">
                      {event.title}
                    </h3>
                    <p className="mt-1 line-clamp-2 text-sm text-text-secondary">
                      {event.description}
                    </p>
                  </div>

                  <dl className="space-y-1.5 text-sm text-text-secondary">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 shrink-0 text-text-muted" />
                      <span>
                        {formatDayDateToLocal(event.start_date, 'EEEE d MMM yyyy')}{' '}
                        <span className="text-text-muted">({getTimezoneAbbreviation()})</span>
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 shrink-0 text-text-muted" />
                      <span className="truncate">{event.location}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 shrink-0 text-text-muted" />
                      <span>
                        {event.current_capacity} {t('events.registered')} / {event.max_capacity}
                      </span>
                    </div>
                  </dl>

                  {isAdmin && (
                    <p className="text-sm text-text-muted">
                      {shiftCount === 0
                        ? 'Nog geen diensten'
                        : `${shiftCount} ${shiftCount === 1 ? 'dienst' : 'diensten'}`}
                    </p>
                  )}

                  <div className="mt-auto flex flex-wrap items-center gap-2 pt-2">
                    <Button
                      variant="secondary"
                      onClick={() => navigate(`/events/${event.id}`)}
                      icon={<ArrowRight className="h-4 w-4" />}
                    >
                      {t('events.viewDetails')}
                    </Button>

                    {isAdmin && (
                      <>
                        <Button
                          variant="secondary"
                          iconOnly
                          aria-label={`Bewerk ${event.title}`}
                          title="Bewerken"
                          onClick={() => navigate(`/events/${event.id}/edit`)}
                          icon={<Edit className="h-4 w-4" />}
                        />
                        <Button
                          variant="danger"
                          iconOnly
                          aria-label={`Verwijder ${event.title}`}
                          title="Verwijderen"
                          onClick={() => setPendingDelete(event)}
                          icon={<Trash2 className="h-4 w-4" />}
                        />
                      </>
                    )}
                  </div>
                </div>
              </Card>
            )
          })}
        </div>
      )}

      <Modal
        open={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        title="Evenement verwijderen"
      >
        {pendingDelete && (pendingDelete.shift_count ?? 0) > 0 ? (
          <>
            <p className="text-md text-text-secondary">
              <span className="font-semibold text-text-primary">{pendingDelete.title}</span> heeft
              nog {pendingDelete.shift_count}{' '}
              {pendingDelete.shift_count === 1 ? 'dienst' : 'diensten'}. Die verdwijnen mee, samen
              met de toewijzingen van vrijwilligers.
            </p>
            <p className="mt-3 text-md text-text-secondary">
              Verplaats of verwijder de diensten eerst.
            </p>
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setPendingDelete(null)}>
                Sluiten
              </Button>
              <Button variant="secondary" onClick={() => navigate('/shifts')}>
                Naar diensten
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-md text-text-secondary">
              Weet je zeker dat je{' '}
              <span className="font-semibold text-text-primary">{pendingDelete?.title}</span> wilt
              verwijderen? Dit kan niet ongedaan worden gemaakt.
            </p>
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setPendingDelete(null)}>
                Annuleren
              </Button>
              <Button variant="danger" onClick={confirmDelete} loading={deleting}>
                Verwijderen
              </Button>
            </div>
          </>
        )}
      </Modal>
    </div>
  )
}

export default EventsPage
